import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import HeaderImage from "../assets/img/Claradécor_header.webp";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

export default function Contact() {
  const form = useRef();
  const [errors, setErrors] = useState({});

  const clearError = (e) => {
    const name = e.target.name;
    if (!name) return;
    if (errors && errors[name]) {
      const next = { ...errors };
      delete next[name];
      setErrors(next);
    }
  };

  const validate = (values) => {
    const errs = {};
    if (!values.name || values.name.trim() === "")
      errs.name = "Veuillez indiquer votre nom.";
    if (!values.firstname || values.firstname.trim() === "")
      errs.firstname = "Veuillez indiquer votre prénom.";
    if (!values.email || values.email.trim() === "")
      errs.email = "Veuillez indiquer votre email.";
    else {
      // simple email regex
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(values.email)) errs.email = "Adresse email invalide.";
    }
    if (!values.message || values.message.trim() === "")
      errs.message = "Veuillez décrire votre demande.";
    // consent checkbox
    if (!values.consent)
      errs.consent = "Vous devez accepter la politique de confidentialité.";

    return errs;
  };

  const sendEmail = (e) => {
    e.preventDefault();

    const formData = new FormData(form.current);
    const values = Object.fromEntries(formData.entries());

    const validation = validate(values);
    setErrors(validation);

    if (Object.keys(validation).length > 0) {
      // there are errors - don't send
      const firstErrorField = document.querySelector(".field-error");
      if (firstErrorField)
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    emailjs
      .sendForm(
        "service_q0kwdpe",
        "template_o29exn1",
        form.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then((result) => {
        console.log(result.text);
        alert("Message envoyé !");
        e.target.reset();
        setErrors({});
      })
      .catch((error) => {
        console.log(error.text);
        alert("Erreur lors de l'envoi !");
      });
  };

  return (
    <div className="contact">
      <Helmet>
        <title>
          Contact – Clara Décor, peintre & décoratrice à Vannes (Morbihan)
        </title>
        <meta
          name="description"
          content="Contactez Clara Décor pour un rendez-vous ou un devis sur-mesure à Vannes et dans le Morbihan. Conseil couleur, diagnostic façade, accompagnement personnalisé et finitions de prestige."
        />
        <link rel="canonical" href="https://claradecor.fr/contact" />
        <meta
          property="og:title"
          content="Contact – Clara Décor, peinture et décoration à Vannes"
        />
        <meta
          property="og:description"
          content="Prenez rendez-vous pour discuter de votre projet : peinture intérieure/exterieure, ravalement, enduits décoratifs et revêtements."
        />
        <meta property="og:url" content="https://claradecor.fr/contact" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://claradecor.fr/images/og-contact.jpg"
        />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact – Clara Décor",
      "url": "https://claradecor.fr/contact",
      "description": "Page de contact de Clara Décor, peintre et décoratrice à Vannes. Prenez rendez-vous pour un devis ou un accompagnement sur-mesure.",
      "mainEntity": {
        "@type": "Organization",
        "name": "Clara Décor",
        "url": "https://claradecor.fr/",
        "logo": "https://claradecor.fr/images/logo.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+33-6-74-81-00-15",
          "contactType": "customer service",
          "areaServed": "Morbihan",
          "availableLanguage": ["fr"]
        }
      }
    }
    `}
        </script>
      </Helmet>

      <Navbar />
      <header>
        <div className="contact-content">
          <div className="contact-left">
            <img src={HeaderImage} alt="Logo de Clara Décors" />
            <p>
              Échangeons ensemble pour donner à{" "}
              <span className="color1">vos idées</span> une réalisation à la
              hauteur de <span className="color1">vos attentes</span> avec un
              devis gratuit.
            </p>
            <form ref={form} onSubmit={sendEmail}>
              <ul>
                <li>
                  <label htmlFor="name"></label>
                  <input
                    type="text"
                    placeholder="Nom *"
                    id="name"
                    name="name"
                    className={errors.name ? "error" : ""}
                    onFocus={clearError}
                  />
                  {errors.name && (
                    <div className="field-error">{errors.name}</div>
                  )}
                </li>
                <li>
                  <label htmlFor="firstname"></label>
                  <input
                    type="text"
                    placeholder="Prénom *"
                    id="firstname"
                    name="firstname"
                    className={errors.firstname ? "error" : ""}
                    onFocus={clearError}
                  />
                  {errors.firstname && (
                    <div className="field-error">{errors.firstname}</div>
                  )}
                </li>
                <li>
                  <label htmlFor="email"></label>
                  <input
                    type="email"
                    placeholder="Email *"
                    id="email"
                    name="email"
                    className={errors.email ? "error" : ""}
                    onFocus={clearError}
                  />
                  {errors.email && (
                    <div className="field-error">{errors.email}</div>
                  )}
                </li>
                <li>
                  <label htmlFor="telephone"></label>
                  <input
                    type="tel"
                    placeholder="Téléphone"
                    id="telephone"
                    name="telephone"
                  />
                </li>
                <li>
                  <label htmlFor="message"></label>
                  <textarea
                    placeholder="Quels sont vos besoins ? *"
                    id="message"
                    name="message"
                    className={errors.message ? "error" : ""}
                    onFocus={clearError}
                  ></textarea>
                  {errors.message && (
                    <div className="field-error">{errors.message}</div>
                  )}
                </li>
              </ul>
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="consent"
                  name="consent"
                  onChange={clearError}
                  className={errors.consent ? "error" : ""}
                />
                <label htmlFor="consent">
                  J'accepte que mes données soient utilisées pour le traitement
                  de ma demande, voir la{" "}
                  <Link to="/politique-de-confidentialite">
                    politique de confidentialité
                  </Link>
                  .
                </label>
              </div>
              {errors.consent && (
                <div id="consent-error" className="field-error">
                  {errors.consent}
                </div>
              )}
              <button type="submit" className="btn-tertiary btn-contact">
                Envoyer
              </button>
            </form>
          </div>
          <div className="contact-right">
            <div className="contact-right-content">
              <h3>
                Clara <br />
                Petit
              </h3>
              <p>
                <FaPhoneAlt
                  className="contact-icon"
                  style={{ color: "#c8b560" }}
                />{" "}
                <a href="tel:+33674810015">06 74 81 00 15</a>
              </p>
              <p>
                <FaEnvelope
                  className="contact-icon"
                  style={{ color: "#c8b560" }}
                />{" "}
                <a href="mailto:claradecor.56@gmail.com">
                  claradecor.56@gmail.com
                </a>
              </p>
              <p>
                <FaMapMarkerAlt
                  className="contact-icon"
                  style={{ color: "#c8b560" }}
                />{" "}
                Vannes & alentours
              </p>
            </div>
          </div>
        </div>
      </header>
      {/* <div className="prefooter"></div> */}
      <Footer />
    </div>
  );
}
